import React from 'react';
import Session from 'store/session';

import jwt_decode from 'jwt-decode';

import md5 from 'md5';
import { getApiPath, getStaticApiPath, getStoredValue, removedStoredValue, setStoredValue } from 'common';

const tokenVar = 'lap-token';

function passwordHash(password) {
  return md5('SmartSalt' + password, {encoding: 'binary'});
}

export class Api {
  constructor(store) {
    this.store = store;
  }

  updateLogin() {
    const token = getStoredValue(tokenVar, () => getStoredValue(tokenVar), true);
    const data = token && jwt_decode(token);
    if (data?.userName) {
      this.store.dispatch.setUser(data.userId, data.userName, data.userLevel || 1);
    } else {
      this.store.dispatch.setUser(0, '', 0);
    }
  }

  authHeaders() {
    const token = getStoredValue(tokenVar, () => getStoredValue(tokenVar), true);
    if (token) {
      return {
        'Authorization': `Bearer ${token}`,
      };
    }
  }

  async fetchAuth(url, options) {
    const response = await fetch(url, {
      ...options,
      headers: this.authHeaders(),
    }).catch(() => null);
    if (response && response.status >= 400 && response.status <= 499) {
      removedStoredValue(tokenVar);
      removedStoredValue(tokenVar, true);
      if (this.store) {
        this.store.dispatch.setUser(0, '', 0);
      }
      return false;
    }
    if (!response?.ok) {
      return null;
    }
    return await response.json().catch(() => null);
  }

  async account(action, params) {
    if (action === 'signout') {
      removedStoredValue(tokenVar);
      removedStoredValue(tokenVar, true);
      if (this.store) {
        this.store.dispatch.setUser(0, '', 0);
      }
      return;
    }

    const data = new FormData();
    data.set('action', action);

    if (params) {
      if (params.name != null) data.set('user_name', params.name);
      if (params.email) data.set('user_email', params.email);
      if (params.reset_code != null) data.set('reset_code', params.reset_code);
      if (params.password != null && (params.password || action !== "options")) data.set('password_hash', passwordHash(params.password));
      if (params.password_old != null) data.set('password_old_hash', passwordHash(params.password_old));
      //if (params.remember) data.set('user_remember', 'true');
      if (params.password_old != null) data.set('user_name', this.store.getState().userName || "");
    }

    const result = await this.fetchAuth(getApiPath('account'), {
      method: 'POST',
      body: data,
    });

    if (result?.token) {
      setStoredValue(tokenVar, result.token, !params?.remember);
      this.updateLogin();
    } else if (!result || result.error || result.errors) {
      throw Error(result?.errors?.[0] || result?.error || `Unknown error.`);
    }

    return true;
  }

  async save(data, params) {
    const url = new URL(getStaticApiPath('save'), window.location);
    if (params) url.search = new URLSearchParams(params).toString();

    const result = await this.fetchAuth(url, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!result || !result.id) {
      throw Error(result?.errors?.[0] || `Failed to save profile.`);
    }

    return result.id;
  }

  async load(id) {
    const result = await fetch(getStaticApiPath(`load/${id}`), {
      method: 'GET',
    }).then(response => response.json()).catch(() => null);

    if (!result || result.error) {
      throw Error(result?.error || `Unknown error`);
    }

    return result;
  }

  async delete(id) {
    const url = new URL(getStaticApiPath('delete'), window.location);
    url.searchParams.append('id', id);
    const result = await this.fetchAuth(url, {
      method: 'POST',
    });

    if (!result || result.code !== 'OK') {
      throw Error(result?.errors?.[0] || `Failed to delete profile.`);
    }

    return true;
  }

  async search(params) {
    const url = new URL(params ? getStaticApiPath('search') : getApiPath('search'), window.location);
    if (params) url.search = new URLSearchParams(params).toString();

    const result = await this.fetchAuth(url, {
      method: 'GET',
    });

    if (!result || !result.results) {
      throw Error(result?.errors?.[0] || `Query failed.`);
    }

    return result;
  }

  async folders(action, params) {
    // no action: GET
    // action=save: POST with JSON body
    // other actions: POST with form-data

    const url = new URL(getApiPath('folders'), window.location);
    if (!action) {
      const result = await this.fetchAuth(url, {
        method: 'GET',
      });
  
      if (!result || result.error) {
        throw Error(result?.error || `Failed to load folders.`);
      }
  
      return result;
    }

    let body = null;
    if (action === 'save') {
      url.searchParams.append('action', action);
      body = JSON.stringify(params);
    } else {
      body = new URLSearchParams({action, ...params});
    }

    const result = await this.fetchAuth(url, {
      method: 'POST',
      body,
    });

    if (!result || result.code !== 'OK') {
      throw Error(result?.error || `Failed to modify folders.`);
    }

    return true;
  }
}

export default function useApi() {
  const store = Session.useStore();
  return React.useMemo(() => new Api(store), [store]);
}
